/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { ReactElement, useState, useEffect } from "react"
import { useFormik } from "formik"
import { Button, Box, Grid, TextField, InputAdornment } from "@material-ui/core"
import { useHistory } from "react-router-dom"
import { BaseCFCStruct, CashFlowRow } from "../../../data/_config/shape"
import { initialValues } from "."
import { PanelsTitle } from "../../CFC"
import Spacer from "../../Spacer/Spacer"
import { routeVarReplacement, PrivateRoutes } from "../../../util/routes/routes"
import useCurrentClient from "../../../state/client/useCurrentClient"
import useStyles from "./__config/styles"
import formatNumber from "../../../util/formatNumber"
import ExpandableNav from "../../ExpandableNav"
import onCreate from "./onCreate"
import { useParams } from "react-router-dom"
import CFCUseCase from "../../../data/CFC/CFCLogic"
import { getMounthDay, monthDiff } from "./table/DataGenerator"

/**
 * Create canvas form.
 *
 * @export
 * @returns {ReactElement}
 */
export default function Step6Form({
	osveziMaster,
	values,
}: {
	osveziMaster: (values: any) => void
	values: any
}): ReactElement {
	const styles = useStyles()
	const { id: canvasId } = useParams<any>()

	const [useCustomTitle, setUseCustomTitle] = useState(false)
	const history = useHistory()
	const [currentClient] = useCurrentClient()
	// const podzemnoSpratnost = 2
	// const nadzemnoSpratnost = 6

	const {
		setFieldValue,
		handleChange,
		// values,
		handleSubmit,
	} = useFormik<BaseCFCStruct>({
		initialValues,
		onSubmit: async (formValues: BaseCFCStruct) => {},
	})

	const {
		step1Items,
		step1CalculatedData,
		step2Items,
		step3Items,
		ukupniTroskovi,
		ukupniPrihodi,
		ukupnoTrajanje,
		canvasStartDate,
		step4Items,
		step5Items,
		step6Items,
	} = values

	const podzemnoSpratnost = step2Items ? step2Items.spratType : undefined
	const nadzemnoSpratnost =
		step1CalculatedData && step1CalculatedData[10]
			? step1CalculatedData[10][1]
			: undefined //6

	function calculateMonthlyPercentage(start, end, allMonths) {
		const startDate = new Date(start)
		const endDate = new Date(end)

		const startDateMonth = startDate.getMonth()
		const endDateMonth = endDate.getMonth()

		const startDateYear = startDate.getFullYear()
		const endDateYear = endDate.getFullYear()

		const totalMonths =
			(endDateYear - startDateYear) * 12 + (endDateMonth - startDateMonth) + 1

		const monthlyPercentage = { ...allMonths }

		let currentMonth = startDateMonth
		let currentYear = startDateYear

		let totalDays = 0

		while (
			currentYear < endDateYear ||
			(currentYear === endDateYear && currentMonth <= endDateMonth)
		) {
			const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate()

			let days = 0

			if (currentYear === startDateYear && currentMonth === startDateMonth) {
				days = daysInMonth - startDate.getDate() + 1
			} else if (currentYear === endDateYear && currentMonth === endDateMonth) {
				days = endDate.getDate()
			} else {
				days = daysInMonth
			}

			totalDays += days
			currentMonth++

			if (currentMonth > 11) {
				currentMonth = 0
				currentYear++
			}
		}

		currentMonth = startDateMonth
		currentYear = startDateYear

		while (
			currentYear < endDateYear ||
			(currentYear === endDateYear && currentMonth <= endDateMonth)
		) {
			const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate()

			let percentage = 0

			if (currentYear === startDateYear && currentMonth === startDateMonth) {
				percentage = ((daysInMonth - startDate.getDate() + 1) / totalDays) * 100
			} else if (currentYear === endDateYear && currentMonth === endDateMonth) {
				percentage = (endDate.getDate() / totalDays) * 100
			} else {
				percentage = (daysInMonth / totalDays) * 100
			}

			monthlyPercentage[
				`${currentMonth + 1}.${currentYear}`
			] = percentage.toFixed(2)

			currentMonth++

			if (currentMonth > 11) {
				currentMonth = 0
				currentYear++
			}
		}

		return monthlyPercentage
	}

	const generateDate = (troskovi, prihodi, startDate, endDate) => {
		let diff = 0

		if (startDate && endDate) diff = monthDiff(startDate, endDate)
		if (diff === 0) {
			return []
		}
		const rows: CashFlowRow[] = []
		let counter = 1
		let prethodnoSum: number = 0
		let otherSum: number = 0

		for (let kategorija in troskovi) {
			let elementi = troskovi[kategorija]
			for (let i = 0; i < elementi.length; i++) {
				const currentMounth = new Date(startDate.getTime())
				let element = elementi[i]
				if (!element.PocetakDatumski) {
					if (kategorija == "AKTIVNOSTI MIMO GRADNJE") {
						prethodnoSum += +element.Iznos.toFixed(0)
					} else {
						otherSum += +element.Iznos.toFixed(0)
					}

					continue
				}

				const outRow: CashFlowRow = {
					id: counter++,
					title: "PLAĆANJA",
					subtitle: element.Naziv,
					type: "out",
					amount: element.Iznos.toFixed(0),
					mounth: {},
				}

				for (let j = 0; j < diff; j++) {
					outRow.mounth[getMounthDay(currentMounth)] = 0
					currentMounth.setMonth(currentMounth.getMonth() + 1)
				}

				const percentageByMonth = calculateMonthlyPercentage(
					element.PocetakDatumski,
					element.KrajDatumski,
					outRow.mounth
				)

				outRow.mounth = percentageByMonth

				rows.push(outRow)
			}
		}

		counter = 1
		let currentMounth = new Date(startDate.getTime())
		const prethodnoRow: CashFlowRow = {
			id: counter++,
			title: "PLAĆANJA",
			subtitle: "Aktivnosti koje prethode gradnji",
			type: "out",
			amount: prethodnoSum,
			mounth: {},
		}
		prethodnoRow.mounth[getMounthDay(currentMounth)] = 100
		for (let j = 0; j < diff - 1; j++) {
			currentMounth.setMonth(currentMounth.getMonth() + 1)
			prethodnoRow.mounth[getMounthDay(currentMounth)] = 0
		}

		rows.unshift(prethodnoRow)

		counter = 1
		currentMounth = new Date(startDate.getTime())
		const ostaloRow: CashFlowRow = {
			id: counter++,
			title: "PLAĆANJA",
			subtitle: "Ostali radovi - rezerva",
			type: "out",
			amount: otherSum,
			mounth: {},
		}
		ostaloRow.mounth[getMounthDay(currentMounth)] = 100
		for (let j = 0; j < diff - 1; j++) {
			currentMounth.setMonth(currentMounth.getMonth() + 1)
			ostaloRow.mounth[getMounthDay(currentMounth)] = 0
		}

		rows.push(ostaloRow)

		counter = 1
		currentMounth = new Date(startDate.getTime())
		const inRowSS: CashFlowRow = {
			id: counter++,
			title: "PRILIVI",
			subtitle: "Sopstvena sredstva",
			type: "in",
			amount: 0,
			mounth: {},
		}

		for (let j = 0; j < diff; j++) {
			inRowSS.mounth[getMounthDay(currentMounth)] = 0
			currentMounth.setMonth(currentMounth.getMonth() + 1)
		}

		rows.push(inRowSS)

		currentMounth = new Date(startDate.getTime())

		const inRowKredit: CashFlowRow = {
			id: counter++,
			title: "PRILIVI",
			subtitle: "Kredit",
			type: "in",
			amount: 0,
			mounth: {},
		}

		for (let j = 0; j < diff; j++) {
			inRowKredit.mounth[getMounthDay(currentMounth)] = 0
			currentMounth.setMonth(currentMounth.getMonth() + 1)
		}

		rows.push(inRowKredit)

		for (let kategorija in prihodi) {
			let elementi = prihodi[kategorija]
			for (let i = 0; i < elementi.length; i++) {
				const currentMounth = new Date(startDate.getTime())
				let element = elementi[i]

				const inRow: CashFlowRow = {
					id: counter++,
					title: "PRILIVI",
					subtitle: element.title + " " + element.Naziv,
					type: "in",
					amount: element.Iznos.toFixed(0),
					mounth: {},
				}

				for (let j = 0; j < diff; j++) {
					inRow.mounth[getMounthDay(currentMounth)] = 0 //(100 / diff).toFixed(2)
					currentMounth.setMonth(currentMounth.getMonth() + 1)
				}

				rows.push(inRow)
			}
		}

		return rows
	}

	const onPrev = async () => {
		if (currentClient?.id) {
			values.step6Items = { troskoviRows, trajanjeRows, prihodiRows }

			let id = canvasId

			id && id != ":id"
				? await CFCUseCase.update(canvasId, values)
				: (id = await onCreate(values, currentClient.id))
			// const query = createURLParams(formValues, useCustomTitle)
			const query = ""
			// eslint-disable-next-line
			history.push(
				`${routeVarReplacement(PrivateRoutes.PlanningStep5, [
					[":id", `${canvasId || id}`],
				])}?${query}`
			)
		}
	}
	const onNext = async () => {
		if (currentClient?.id) {
			values.step6Items = { troskoviRows, trajanjeRows, prihodiRows }
			let id = canvasId

			values.canvasEndDate = new Date(canvasStartDate)
			values.canvasEndDate.setDate(
				values.canvasStartDate.getDate() + getTrajanjeRealno() * 30
			)

			values.cashItems = generateDate(
				troskoviRows,
				prihodiRows,
				values.canvasStartDate,
				values.canvasEndDate
			)
			values.currentPlanMounth = getMounthDay(values.canvasStartDate)
			values.canvasType = "planirani"

			id && id != ":id"
				? await CFCUseCase.update(canvasId, values)
				: (id = await onCreate(values, currentClient.id))
			// const query = createURLParams(formValues, useCustomTitle)
			const query = ""
			// eslint-disable-next-line
			history.push(
				`${routeVarReplacement(PrivateRoutes.CFCEdit, [
					[":id", `${canvasId || id}`],
				])}?${query}`
			)
		}
	}

	useEffect(() => {
		if (!useCustomTitle) {
			setFieldValue("canvasTitle", "")
		}
	}, [useCustomTitle, setFieldValue])

	function generisiNizTrajanje(initPodaci) {
		const includeTrajanjeItems = [
			"Šipovi",
			"Zaštita temeljne jame sa iskopom i HI",
			"AB radovi - podzemno",
			"AB radovi - nadzemno",
			"Spoljno uređenje",
		]

		const trajanje = {}
		const kategorijaTrajanje = []

		for (const kategorija in initPodaci) {
			const aktivnosti = initPodaci[kategorija]
			for (const aktivnost of aktivnosti) {
				const { Naziv, Trajanje2, Trajanje } = aktivnost
				if (Trajanje2 > 0 && includeTrajanjeItems.includes(Naziv)) {
					kategorijaTrajanje.push({
						Naziv,
						Interval: 0,
						Iznos:
							Naziv == "AB radovi - nadzemno"
								? Trajanje2 + Trajanje * 10
								: Trajanje2,
						RealanIznos: null,
					})
				}
			}
		}

		trajanje[""] = kategorijaTrajanje

		return trajanje
	}

	const updateSecondArray = (mainObj, otherObj) => {
		const mainKeys = Object.keys(mainObj)

		if (!mainKeys.length || !Object.keys(otherObj).length) {
			console.log("Nedostaju podaci u nizovima.")
			return
		}

		const updatedArray = {}

		for (const key of mainKeys) {
			if (key in mainObj && key in otherObj) {
				const mainArray = mainObj[key]
				const otherArray = otherObj[key]

				const updatedSubArray = otherArray.map((item) => {
					const correspondingItem = mainArray.find(
						(el) => el.Naziv === item.Naziv && el.Interval !== ""
					)

					if (correspondingItem) {
						item.Interval = correspondingItem.Interval
					}

					return item
				})

				updatedArray[key] = updatedSubArray
			}
		}

		return updatedArray
	}

	const [troskoviRows, setTroskoviRows] = useState(
		step6Items
			? updateSecondArray(step6Items.troskoviRows, step3Items)
			: step3Items
	)

	const [trajanjeRows, setTrajanjeRows] = useState(
		step6Items
			? updateSecondArray(
					step6Items.trajanjeRows,
					generisiNizTrajanje(step4Items)
			  )
			: generisiNizTrajanje(step4Items)
	)

	const podzemnoItems = step5Items.podzemno.map((item) => ({
		...item,
		title: "Garaža",
	}))
	const nadzemnoItems = step5Items.nadzemno.map((item) => ({
		...item,
		title:
			item.Naziv == "Prizemlje" || item.Naziv == "Potkrovlje" ? "" : "Sprat",
	}))

	const [prihodiRows, setPrihodiRows] = useState(
		step6Items
			? updateSecondArray(
					step6Items.prihodiRows,
					step5Items && step5Items.podzemno
						? {
								"": podzemnoItems.concat(nadzemnoItems),
						  }
						: []
			  )
			: step5Items && step5Items.podzemno
			? {
					"": podzemnoItems.concat(nadzemnoItems),
			  }
			: []
	)

	const monteCarloSimulacija = (podaci, brojIteracija, forumla) => {
		const newData = { ...podaci }
		for (let kategorija in podaci) {
			let troskoviKategorije = newData[kategorija]
			for (let item of troskoviKategorije) {
				let ukupanRealanIznos = 0

				for (let i = 0; i < brojIteracija; i++) {
					let simulacijaRealanIznosa = 0

					const rnd = Math.random()
					const interval =
						item.Interval === null ? 0 : parseFloat(item.Interval) / 100
					const Iznos = parseFloat(item.Iznos)

					if (item.Interval) {
						simulacijaRealanIznosa += calculateRealanIznos(
							Iznos,
							interval,
							rnd,
							forumla
						)
					} else {
						simulacijaRealanIznosa += Iznos
					}

					ukupanRealanIznos += simulacijaRealanIznosa
				}

				const prosecniRealanIznos = ukupanRealanIznos / brojIteracija
				item.RealanIznos = prosecniRealanIznos // Ažurirajte polje "RealanIznos" za trenutnu stavku
			}
		}
		return newData
	}

	for (const groupKey in troskoviRows) {
		if (troskoviRows.hasOwnProperty(groupKey)) {
			const group = troskoviRows[groupKey]

			// Prođite kroz sve stavke u grupi
			for (const item of group) {
				item.rnd = parseFloat(Math.random().toFixed(2))
			}
		}
	}

	const calculateRealanIznos = (Iznos, interval, rnd, formula) => {
		if (interval === null) {
			return parseFloat(Iznos)
		}
		return parseFloat(Iznos) * (1 + formula * (parseFloat(interval) * rnd))
	}

	const calculateTotalAmountGrupe = (groupData, kolona) => {
		let totalAmount = 0

		if (Array.isArray(groupData)) {
			for (const item of groupData) {
				totalAmount += item[kolona] || 0
			}
		}

		return totalAmount
	}

	const calculateTotalAmount = (groupData, kolona) => {
		let totalAmount = 0

		for (const groupKey in groupData) {
			const group = groupData[groupKey]
			for (const item of group) {
				totalAmount += item[kolona] || 0
			}
		}

		return totalAmount
	}

	const Tabela = ({ rows, setRows, Interval }) => {
		return (
			<table>
				<thead>
					<tr>
						<th
							style={{
								textAlign: "left",
								// width: "30%",
							}}
						></th>
						<th
							style={{
								textAlign: "left",
								width: "25%",
							}}
						>
							{Interval}
						</th>
						{/* <th
							style={{
								textAlign: "left",
								width: "15%",
							}}
						>
							Optimistički
						</th>
						<th
							style={{
								textAlign: "left",
								width: "15%",
							}}
						>
							Realno
						</th> */}
					</tr>
				</thead>
				<tbody>
					{Object.keys(rows).map((group, groupIndex) => (
						<React.Fragment key={groupIndex}>
							<tr>
								<th>{group}</th>
							</tr>
							{rows[group].map((item, itemIndex) => {
								return (
									<tr key={itemIndex}>
										<td>{item.Naziv}</td>
										<td>
											<input
												type="number"
												style={{
													width: "75%",
												}}
												defaultValue={item.Interval}
												onBlur={(e) => {
													const newData = { ...rows }
													newData[group][itemIndex].Interval = e.target.value
													setRows(newData)
												}}
											/>
										</td>
										{/* <td>{formatNumber(item.Iznos || 0)}</td>
										<td>{formatNumber(item.RealanIznos || 0)}</td> */}
									</tr>
								)
							})}
							{/* {calculateTotalAmountGrupe(rows[group], "Iznos") != 0 ? (
								<tr>
									<td colSpan={2} style={{ textAlign: "right" }}>
										Suma:
									</td>
									<td>
										{formatNumber(
											calculateTotalAmountGrupe(rows[group], "Iznos")
										)}
									</td>
									<td>
										{formatNumber(
											calculateTotalAmountGrupe(rows[group], "RealanIznos")
										)}
									</td>
								</tr>
							) : undefined} */}
						</React.Fragment>
					))}
					{/* <tr>
						<td colSpan={2} style={{ textAlign: "right" }}>
							Ukupno:
						</td>
						<td>{formatNumber(calculateTotalAmount(rows, "Iznos"))}</td>
						<td>{formatNumber(calculateTotalAmount(rows, "RealanIznos"))}</td>
					</tr> */}
				</tbody>
			</table>
		)
	}

	const startMonteCarlo = () => {
		const brojIteracija = 1000
		const simulacijaTroskovi = monteCarloSimulacija(
			troskoviRows,
			brojIteracija,
			1
		)
		setTroskoviRows(simulacijaTroskovi)

		const simulacijaTrajanje = monteCarloSimulacija(
			trajanjeRows,
			brojIteracija,
			1
		)
		setTrajanjeRows(simulacijaTrajanje)

		const simulacijaPrihodi = monteCarloSimulacija(
			prihodiRows,
			brojIteracija,
			-1
		)
		setPrihodiRows(simulacijaPrihodi)
	}

	const pronadjiVrednostZaAktivnost = (podaci, nazivAktivnosti, atribut) => {
		// Prolazimo kroz sve ključeve u objektu podaci
		for (const kategorijaNaziv in podaci) {
			if (Object.hasOwnProperty.call(podaci, kategorijaNaziv)) {
				const kategorija = podaci[kategorijaNaziv]

				// Prolazimo kroz sve elemente u ovoj kategoriji
				for (let i = 0; i < kategorija.length; i++) {
					const element = kategorija[i]

					// Proverite da li je Naziv jednak nazivAktivnosti
					if (element.Naziv === nazivAktivnosti) {
						// Ako je Naziv jednak, vratite vrednost Trajanje iz tog elementa
						return element[atribut]
					}
				}
			}
		}

		// Ako aktivnost nije pronađena, možete vratiti neku podrazumevanu vrednost ili null
		return null
	}

	const getTrajanjeRealno = () => {
		// const kraj = pronadjiVrednostZaAktivnost(
		// 	trajanjeRows,
		// 	"Spoljno uređenje",
		// 	"RealanIznos"
		// )
		// if (+kraj > 0) {
		// 	return ((+kraj - 1) / 30).toFixed(2)
		// } else {
		// 	return ukupnoTrajanje
		// }

		// Inicijalizujemo promenljivu za sumu
		var sumaRealnihIznosa = 0

		// Iteriramo kroz niz objekata
		trajanjeRows[""].forEach(function (element) {
			// Dodajemo vrednost RealanIznos atributa u sumu
			sumaRealnihIznosa += element.RealanIznos
		})

		if (+sumaRealnihIznosa > 0) {
			return ((+sumaRealnihIznosa - 1) / 30).toFixed(2)
		} else {
			return ukupnoTrajanje
		}
	}

	const OptimistickaMasterTabela = ({}) => {
		return (
			<ExpandableNav title="Optimistički" reactourLabel="cfc-four-questions">
				<TextField
					value={formatNumber(ukupniTroskovi)}
					name="1"
					variant="outlined"
					label="Troškovi"
					disabled={true}
					InputProps={{
						startAdornment: <InputAdornment position="start">€</InputAdornment>,
						endAdornment: <InputAdornment position="end"> </InputAdornment>,
					}}
				/>
				<Spacer />
				<TextField
					value={formatNumber(ukupniPrihodi)}
					name="1"
					variant="outlined"
					label="Prihodi"
					disabled={true}
					InputProps={{
						startAdornment: <InputAdornment position="start">€</InputAdornment>,
						endAdornment: <InputAdornment position="end"> </InputAdornment>,
					}}
				/>
				<Spacer />
				<TextField
					value={formatNumber((ukupniPrihodi || 0) - (ukupniTroskovi || 0))}
					name="1"
					variant="outlined"
					label="Profit"
					disabled={true}
					InputProps={{
						startAdornment: <InputAdornment position="start">€</InputAdornment>,
						endAdornment: <InputAdornment position="end"> </InputAdornment>,
					}}
				/>
				<Spacer />
				<TextField
					value={(ukupnoTrajanje || 0).toFixed(2)}
					name="1"
					variant="outlined"
					label="Vreme"
					disabled={true}
					InputProps={{
						startAdornment: <InputAdornment position="start">M</InputAdornment>,
						endAdornment: <InputAdornment position="end"> </InputAdornment>,
					}}
				/>
			</ExpandableNav>
		)
	}

	const RealnaMasterTabela = ({}) => {
		return (
			<ExpandableNav title="Realno" reactourLabel="cfc-four-questions">
				<TextField
					value={formatNumber(
						calculateTotalAmount(troskoviRows, "RealanIznos")
					)}
					name="1"
					variant="outlined"
					label="Troškovi"
					disabled={true}
					InputProps={{
						startAdornment: <InputAdornment position="start">€</InputAdornment>,
						endAdornment: <InputAdornment position="end"> </InputAdornment>,
					}}
				/>
				<Spacer />
				<TextField
					value={formatNumber(calculateTotalAmount(prihodiRows, "RealanIznos"))}
					name="1"
					variant="outlined"
					label="Prihodi"
					disabled={true}
					InputProps={{
						startAdornment: <InputAdornment position="start">€</InputAdornment>,
						endAdornment: <InputAdornment position="end"> </InputAdornment>,
					}}
				/>
				<Spacer />
				<TextField
					value={formatNumber(
						calculateTotalAmount(prihodiRows, "RealanIznos") -
							calculateTotalAmount(troskoviRows, "RealanIznos")
					)}
					name="1"
					variant="outlined"
					label="Profit"
					disabled={true}
					InputProps={{
						startAdornment: <InputAdornment position="start">€</InputAdornment>,
						endAdornment: <InputAdornment position="end"> </InputAdornment>,
					}}
				/>
				<Spacer />
				<TextField
					value={formatNumber(getTrajanjeRealno())}
					name="1"
					variant="outlined"
					label="Vreme"
					disabled={true}
					InputProps={{
						startAdornment: <InputAdornment position="start">M</InputAdornment>,
						endAdornment: <InputAdornment position="end"> </InputAdornment>,
					}}
				/>
			</ExpandableNav>
		)
	}

	return (
		<>
			<Box className={styles.box}>
				<Button
					color="primary"
					variant="outlined"
					type="submit"
					onClick={startMonteCarlo}
				>
					Monte Carlo Simulacija
				</Button>
				<Button
					color="primary"
					variant="outlined"
					type="submit"
					onClick={onPrev}
				>
					Prihodi
				</Button>
				<Button
					color="primary"
					variant="outlined"
					type="submit"
					onClick={onNext}
				>
					Kreiraj Cash Flow
				</Button>
			</Box>
			<form onSubmit={handleSubmit} data-reactour="create-a-canvas">
				<PanelsTitle customTitle={"Rizici"} useCustomTitle={true} />
				<Spacer />
				<Spacer />
				<Box className={styles.box}>
					<Grid
						container
						spacing={1}
						// alignItems="center"
						justify="center"
						// alignContent="center"
					>
						<Grid item xs={3}>
							<PanelsTitle customTitle={"Troškovi"} useCustomTitle={true} />
							<Tabela
								rows={troskoviRows}
								setRows={setTroskoviRows}
								Interval={"Porast troškova u %"}
							/>
						</Grid>
						<Grid item xs={1}></Grid>
						<Grid item xs={3}>
							<PanelsTitle customTitle={"Trajanje"} useCustomTitle={true} />
							<Tabela
								rows={trajanjeRows}
								setRows={setTrajanjeRows}
								Interval={"Produžetak trajanja u %"}
							/>
							<div style={{ marginTop: "60px" }}></div>
							<PanelsTitle customTitle={"Prihodi"} useCustomTitle={true} />
							<Tabela
								rows={prihodiRows}
								setRows={setPrihodiRows}
								Interval={"Niža prodajna cena u %"}
							/>
						</Grid>
						<Grid item xs={1}></Grid>
						<Grid item xs={2}>
							<OptimistickaMasterTabela></OptimistickaMasterTabela>
							<RealnaMasterTabela></RealnaMasterTabela>
						</Grid>
					</Grid>
				</Box>
			</form>
		</>
	)
}
